import { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import {
  ADMINISTRATOR_ROLE,
  BUDGET_COORDINATOR_ROLE,
  BUDGET_OWNER_ROLE,
  HR_COORDINATOR_ROLE,
} from "common/context/auth/roles";
import {
  SECTION_ANALYSIS,
  SECTION_BUDGET,
  SECTION_BUDGET_SETTINGS,
  SECTION_EMPLOYEE_MANAGEMENT,
  SECTION_OVERVIEW,
  SECTION_USERS,
  SECTION_WORKFORCE_SETTINGS,
} from "common/routes/sections/main.sections";
import { FullPageSpinner } from "common/components/full-page/full-page-spinner";
import { useAuth } from "common/context/auth/auth-context";

const BudgetSettings = lazy(() =>
  import("features/budget-settings/views/budget-settings"),
);
const Overview = lazy(() => import("features/overview/overview.component"));
const Budget = lazy(() =>
  import("features/budget/wizard/views/budget.container"),
);
const CoordinatorUser = lazy(() =>
  import("features/coordinator-user/views/coordinator-user.consumer"),
);
const WorkforceSettings = lazy(() =>
  import("features/workforce/workforce-settings/views/workforce-settings"),
);
const EmployeeManagement = lazy(() =>
  import("features/workforce/employee-management/views/employee-management"),
);
const Analysis = lazy(() => import("features/analysis/analysis"));

function ProtectedAppRoutes() {
  const { user } = useAuth();
  const userRole = user === null ? null : user.roles[0]?.value ?? null;
  if (userRole === null) {
    return null;
  }

  let routes = null;
  if (userRole === ADMINISTRATOR_ROLE) {
    routes = (
      <Switch>
        <Route strict path={`/${SECTION_BUDGET_SETTINGS.path}`}>
          <BudgetSettings />
        </Route>
        <Route strict path={`/${SECTION_USERS.path}`}>
          <CoordinatorUser />
        </Route>
        <Redirect to={`/${SECTION_BUDGET_SETTINGS.path}`} />
      </Switch>
    );
  } else if (userRole === BUDGET_COORDINATOR_ROLE) {
    routes = (
      <Switch>
        <Route strict path={`/${SECTION_OVERVIEW.path}`}>
          <Overview />
        </Route>
        <Route strict path={`/${SECTION_BUDGET.path}`}>
          <Budget />
        </Route>
        <Route strict path={`/${SECTION_USERS.path}`}>
          <CoordinatorUser />
        </Route>
        <Route strict path={`/${SECTION_WORKFORCE_SETTINGS.path}`}>
          <WorkforceSettings />
        </Route>
        <Route strict path={`/${SECTION_ANALYSIS.path}`}>
          <Analysis />
        </Route>
        <Redirect to={`/${SECTION_OVERVIEW.path}`} />
      </Switch>
    );
  } else if (userRole === BUDGET_OWNER_ROLE) {
    routes = (
      <Switch>
        <Route strict path={`/${SECTION_OVERVIEW.path}`}>
          <Overview />
        </Route>
        <Redirect to={`/${SECTION_OVERVIEW.path}`} />
      </Switch>
    );
  } else if (userRole === HR_COORDINATOR_ROLE) {
    routes = (
      <Switch>
        <Route strict path={`/${SECTION_WORKFORCE_SETTINGS.path}`}>
          <WorkforceSettings />
        </Route>
        <Route strict path={`/${SECTION_EMPLOYEE_MANAGEMENT.path}`}>
          <EmployeeManagement />
        </Route>
        <Redirect to={`/${SECTION_WORKFORCE_SETTINGS.path}`} />
      </Switch>
    );
  }

  return <Suspense fallback={<FullPageSpinner />}>{routes}</Suspense>;
}

export default ProtectedAppRoutes;
